import { AlertColor, DialogContent, Drawer } from "@mui/material";
import { Formik, FormikValues } from "formik";
import ErrorRibbon from "../../../common/ErrorRibbon";
import WriteableSolutionFormContainer from "../../common/form-components";
import { DetailedSolutionApiResponse } from "../../types";
import { solutionForm } from "../../formSchema";

type Alert = {
  type: AlertColor;
  message: string;
  errorCode?: number;
} | null;

type Props = {
  isEditing: boolean;
  handleEdit: (value: boolean) => void;
  updateSolution: (formData: FormikValues) => void;
  solutionDetails: DetailedSolutionApiResponse;
  loading: boolean;
  errorAlert: {
    details: Alert;
    setAlert: (alert: Alert) => void;
  };
};

function generateUpdateFormInitialValues(
  solutionDetails: DetailedSolutionApiResponse,
) {
  return {
    "solution-name": solutionDetails.name || "",
    "solution-vbc-website-display-name":
      solutionDetails.vbcWebsiteDisplayName || "",
    "solution-developer": solutionDetails.developer || "",
    "solution-short-description": solutionDetails.shortDescription || "",
    "country-select": solutionDetails.country || [],
    "solution-development-stage": solutionDetails.developmentStage || [],
    "solution-assessment-methodology":
      solutionDetails.assessmentMethodology || null,
    "solution-intervention-type": solutionDetails.interventionType || null,
    "solution-is-ready-for-sale": solutionDetails.isReadyForSale || false,
    "solution-start-date": solutionDetails.date?.start || null,
    "solution-end-date": solutionDetails.date?.end || null,
    "solution-is-carbon-offset":
      solutionDetails.isUsedForCarbonOffsetting || false,
    latitude: solutionDetails.coordinates?.latitude || "",
    longitude: solutionDetails.coordinates?.longitude || "",
    "solution-sdg": solutionDetails.SDG || [],
    interventions:
      solutionDetails.interventions?.map((intervention) => ({
        action: intervention?.action,
        ecosystem: intervention?.ecosystem,
      })) || [],
    outcomes:
      solutionDetails.outcomes?.v3?.map((outcome, index) => ({
        unit: outcome.unit,
        value: outcome.value,
        "unit-of-sale": outcome.isUnitOfSale,
        "outcome-start-date": outcome.startDate ?? 0,
        "outcome-end-date": outcome.endDate ?? null,
        outcomeCounter: index + 1,
        origin: "saved" as const,
      })) || [],
    "outcomes-v2": solutionDetails.outcomes?.v2 || [],
    "programme-info": {
      "carbon-crediting-programme":
        solutionDetails?.programmeInfo?.carbonCreditProgramme || [],
      "carbon-accounting-methodology":
        solutionDetails?.programmeInfo?.accountingMethodology || "",
      "description-document":
        solutionDetails?.programmeInfo?.projectDescriptionDocument || "",
      "asset-type": solutionDetails.programmeInfo?.assetType || null,
    },
    "internal-details": {
      "website-url": solutionDetails?.internalUseOnly?.websiteUrl || "",
      "xero-account-number":
        solutionDetails?.internalUseOnly?.xeroAccountNumber || "",
      "research-team-notes":
        solutionDetails?.internalUseOnly?.researchTeamNotes || "",
      "additional-resources-link":
        solutionDetails?.internalUseOnly?.additionalResourceLink || "",
    },
    "risk-monitoring": {
      "is-risk-alert-enabled":
        solutionDetails?.riskMonitoring?.isEnabled || false,
      "buffer-value-km": solutionDetails?.riskMonitoring?.buffer || 10,
      "geospatial-file":
        solutionDetails?.riskMonitoring?.geospatialFile || null,
      "geospatial-id":
        solutionDetails?.riskMonitoring?.geospatialDataId || null,
    },
  };
}

export function UpdateSolutionDetailsDrawer({
  loading,
  isEditing,
  handleEdit,
  updateSolution,
  solutionDetails,
  errorAlert,
}: Props) {
  return (
    <Drawer
      data-testid="update-solution-drawer"
      anchor="bottom"
      open={isEditing}
    >
      <DialogContent>
        <ErrorRibbon data={errorAlert} />
        <Formik
          initialValues={generateUpdateFormInitialValues(solutionDetails)}
          validationSchema={solutionForm}
          onSubmit={(form) => updateSolution(form)}
          validateOnMount
          initialTouched={{
            "solution-name": true,
            "solution-vbc-website-display-name": true,
            "solution-developer": true,
            "solution-assessment-methodology": true,
            "solution-intervention-type": true,
            "solution-short-description": true,
            "solution-development-stage": true,
            "solution-is-ready-for-sale": true,
            "solution-start-date": true,
            "solution-end-date": true,
            latitude: true,
            longitude: true,
            interventions: [
              {
                action: true,
                ecosystem: true,
              },
            ],
            "internal-details": {
              "website-url": true,
            },
            "programme-info": {
              "asset-type": true,
              "carbon-crediting-programme": true,
            },
            "risk-monitoring": {
              "is-risk-alert-enabled": false,
              "buffer-value-km": false,
              "geospatial-file": false,
              "geospatial-id": false,
            },
          }}
        >
          <WriteableSolutionFormContainer
            submitLoading={loading}
            handleCancel={() => {
              errorAlert.setAlert(null);
              handleEdit(false);
            }}
            title="Edit Project"
            page="EditSolutionPage"
          />
        </Formik>
      </DialogContent>
    </Drawer>
  );
}
